import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { getSupabaseClient } from "./supabase";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const sale = await getSale();

      if (sale == null) {
        throw new Error();
      }

      return {
        id: sale.id,
        fullName: `${sale.first_name} ${sale.last_name}`,
        avatar: sale.avatar?.src,
        roleKey: sale.roles?.key ?? null,
        roleName: sale.roles?.name,
        permissions: sale.roles?.permissions ?? [],
        // the developer's own role while viewing the CRM as another one
        realRoleKey: sale.real_role?.key ?? sale.roles?.key ?? null,
        previewRoleId: sale.preview_role_id ?? null,
      };
    },
  });

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
// v3: { at, sale }, the sale embeds its role and permissions
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale_v3";
// A role changed by the owner reaches an open session within this time.
const SALE_CACHE_TTL_MS = 60_000;

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export async function getIsInitialized() {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

const getSale = async () => {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (cachedValue != null) {
    try {
      const cached = JSON.parse(cachedValue);
      if (Date.now() - cached.at < SALE_CACHE_TTL_MS) return cached.sale;
    } catch {
      // unreadable cache: fetch again
    }
  }

  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }

  const { data: dataSale, error: errorSale } = await getSupabaseClient()
    .from("sales")
    .select(
      "id, first_name, last_name, avatar, administrator, role_id, preview_role_id, roles(key, name, permissions)",
    )
    .match({ user_id: dataSession?.session?.user.id })
    .single();

  // Shouldn't happen either as all users are sales but just in case
  if (dataSale == null || errorSale) {
    return undefined;
  }

  // Developer viewing as another role: the interface takes that role, the
  // database does the same (current_permissions), the real role is kept aside
  if (
    dataSale.preview_role_id != null &&
    (dataSale.roles as unknown as { key?: string } | null)?.key === "developer"
  ) {
    const { data: preview } = await getSupabaseClient()
      .from("roles")
      .select("key, name, permissions")
      .eq("id", dataSale.preview_role_id)
      .single();
    if (preview) {
      Object.assign(dataSale, { real_role: dataSale.roles, roles: preview });
    }
  }

  storage?.setItem(
    CURRENT_SALE_CACHE_KEY,
    JSON.stringify({ at: Date.now(), sale: dataSale }),
  );
  return dataSale;
};

function clearCache() {
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) {
          throw error;
        }
        return;
      }
      return baseAuthProvider.login(params);
    },
    logout: async (params) => {
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkAuth: async (params) => {
      // Users are on the set-password page, nothing to do
      if (
        window.location.pathname === "/set-password" ||
        window.location.hash.includes("#/set-password")
      ) {
        return;
      }
      // Users are on the forgot-password page, nothing to do
      if (
        window.location.pathname === "/forgot-password" ||
        window.location.hash.includes("#/forgot-password")
      ) {
        return;
      }
      // Users are on the sign-up page, nothing to do
      if (
        window.location.pathname === "/sign-up" ||
        window.location.hash.includes("#/sign-up")
      ) {
        return;
      }

      const isInitialized = await getIsInitialized();

      if (!isInitialized) {
        await getSupabaseClient().auth.signOut();
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }

      return baseAuthProvider.checkAuth(params);
    },
    canAccess: async (params) => {
      const isInitialized = await getIsInitialized();
      if (!isInitialized) return false;

      // Get the current user
      const sale = await getSale();
      if (sale == null) return false;

      // Compute access rights from the sale role
      return canAccess(sale.roles?.permissions, params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};

/** Developer only: look at the CRM as another role (null = back to own) */
export const switchViewRole = async (roleId: number | null) => {
  const { error } = await getSupabaseClient().rpc("set_preview_role", {
    role: roleId,
  });
  if (error) throw error;
  getLocalStorage()?.removeItem(CURRENT_SALE_CACHE_KEY);
  // every cached list and permission check starts again under the new role
  window.location.hash = "#/";
  window.location.reload();
};
